import { useState } from "react";
import { Plus, Trash2, Truck, Calendar as CalendarIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Input } from "./ui/input";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { SuggestInput } from "./SuggestInput";
import { classifyZip } from "../lib/deliveryZones";
import { US_STATES } from "../lib/usStates";

export const EMPTY_DELIVERY = {
  event_date: "", event_time: "", address: "", address_line2: "", city: "", state: "NY", zip_code: "", delivery_fee: "",
};

// Delivery fee for one drop: manual override wins, else the ZIP tier.
export const dropFee = (d) =>
  d.delivery_fee !== "" ? Math.max(0, Number(d.delivery_fee) || 0) : (d.zip_code ? classifyZip(d.zip_code)?.fee ?? 0 : 0);

const field = "h-10 rounded-xl bg-white border-black/10";

const DateField = ({ value, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  return (
  <Popover open={open} onOpenChange={setOpen}>
    <PopoverTrigger asChild>
      <button
        type="button"
        data-testid={testid}
        className="flex h-10 w-full items-center justify-between rounded-xl border border-black/10 bg-white px-3 text-left text-[13px]"
      >
        <span className={value ? "text-brand-charcoal" : "text-brand-charcoal/40"}>
          {value ? format(parseISO(value), "EEE, MMM d, yyyy") : "Date"}
        </span>
        <CalendarIcon className="h-4 w-4 text-brand-charcoal/50" />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-auto p-0" align="start">
      <Calendar mode="single" selected={value ? parseISO(value) : undefined} onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }} initialFocus />
    </PopoverContent>
  </Popover>
  );
};

const DeliveryCard = ({ d, idx, itemCount, update, remove, canRemove, email }) => {
  const set = (k) => (v) => update(idx, { [k]: v });
  const zone = d.zip_code ? classifyZip(d.zip_code) : null;
  const tid = `manual-invoice-drop-${idx}`;
  return (
    <div data-testid={tid} className="rounded-2xl border border-black/10 bg-brand-cream/30 p-3.5 space-y-2.5">
      <div className="flex items-center justify-between">
        <p className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.14em] text-brand-charcoal">
          <Truck className="h-3.5 w-3.5 text-brand-teal" /> Delivery {idx + 1}
          <span className="font-bold text-brand-charcoal/50 normal-case tracking-normal">· {itemCount} item{itemCount === 1 ? "" : "s"}</span>
        </p>
        {canRemove && (
          <button type="button" data-testid={`${tid}-remove`} onClick={() => remove(idx)} className="p-1.5 rounded-full text-brand-charcoal/50 hover:text-red-600 hover:bg-red-50" aria-label="Remove delivery">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <DateField value={d.event_date} onChange={set("event_date")} testid={`${tid}-date`} />
        <Input data-testid={`${tid}-time`} type="time" value={d.event_time} onChange={(e) => set("event_time")(e.target.value)} className={field} />
      </div>
      <SuggestInput
        kind="address"
        email={email}
        testid={`${tid}-address`}
        placeholder="Street address"
        value={d.address}
        onChange={set("address")}
        onPick={(s) => update(idx, { address: s.address, address_line2: s.address_line2, city: s.city, state: s.state || "NY", zip_code: s.zip_code })}
      />
      <div className="grid grid-cols-6 gap-2">
        <Input data-testid={`${tid}-address2`} placeholder="Suite / Floor" value={d.address_line2} onChange={(e) => set("address_line2")(e.target.value)} className={`col-span-6 sm:col-span-2 ${field}`} />
        <Input data-testid={`${tid}-city`} placeholder="City" value={d.city} onChange={(e) => set("city")(e.target.value)} className={`col-span-3 sm:col-span-2 ${field}`} />
        <select data-testid={`${tid}-state`} value={d.state} onChange={(e) => set("state")(e.target.value)} className="col-span-1 h-10 rounded-xl bg-white border border-black/10 px-1 text-[13px]">
          {US_STATES.map(([code]) => <option key={code} value={code}>{code}</option>)}
        </select>
        <Input data-testid={`${tid}-zip`} placeholder="ZIP" value={d.zip_code} onChange={(e) => set("zip_code")(e.target.value.replace(/\D/g, "").slice(0, 5))} className={`col-span-2 sm:col-span-1 ${field}`} />
      </div>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-charcoal/50 text-[13px] font-bold">$</span>
        <Input
          data-testid={`${tid}-fee`}
          type="number" min="0" step="0.01" inputMode="decimal"
          placeholder={zone?.fee != null ? `${zone.fee.toFixed(2)} (auto from ZIP)` : "Delivery fee"}
          value={d.delivery_fee}
          onChange={(e) => set("delivery_fee")(e.target.value)}
          className={`pl-7 ${field}`}
        />
      </div>
    </div>
  );
};

export const DeliveriesEditor = ({ deliveries, setDeliveries, items, email, onRemove }) => {
  const update = (idx, patch) => setDeliveries((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  // Only the last delivery is removable; its items move to the previous one.
  const remove = (idx) => {
    setDeliveries((prev) => prev.filter((_, i) => i !== idx));
    onRemove(idx);
  };
  return (
    <div data-testid="manual-invoice-deliveries" className="space-y-3">
      {deliveries.map((d, idx) => (
        <DeliveryCard
          key={idx}
          d={d}
          idx={idx}
          email={email}
          itemCount={items.filter((it) => it.delivery_index === idx).length}
          update={update}
          remove={remove}
          canRemove={deliveries.length > 2 && idx === deliveries.length - 1}
        />
      ))}
      {deliveries.length < 10 && (
        <button
          type="button"
          data-testid="manual-invoice-add-drop"
          onClick={() => setDeliveries((prev) => [...prev, { ...EMPTY_DELIVERY, event_date: prev[prev.length - 1]?.event_date || "" }])}
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-brand-charcoal/25 px-3 py-2.5 text-[11px] font-bold uppercase tracking-[0.1em] text-brand-charcoal hover:border-brand-teal hover:text-brand-teal"
        >
          <Plus className="h-3.5 w-3.5" /> Add another delivery
        </button>
      )}
    </div>
  );
};

export default DeliveriesEditor;
